import React, { useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
// Actions
import {
  searchRoles,
  sendGenericExport,
  clearRedux,
  getCurrentSubUser
} from "Redux/actions";
// Components
import { Pill } from 'Components';
import { Table, SearchInput, Toolbar, Button } from 'Components/uiControls';
import { TableButtons } from 'Components/uiControls/Table';
import { Grid, Grow, Container, Typography } from '@mui/material';
import { MenuComponent } from 'Components/Menu';
import CreditEditRol from './components/creditEditRole'
import DeleteRole from './components/deleteRole'
import { usePagination } from 'Components/uiControls/Table/hooks';
import { NotificationManager } from 'Components/Notifications';
import { HasPermission } from "Core/security"
// Style Global
import styles from 'Assets/sass/core/vars.scss';
import { useSupervisorPermission } from '../../../.common/utils/supervisorPermission';

const Role = () => {
  const { messages, locale } = useIntl();
  const dispatch = useDispatch();
  const tableRef = useRef();

  // State local
  const [showModalCrud, setShowModalCrud] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [roleSelected, setRoleSelected] = useState();
  const [rolesToRemove, setRolesToRemove] = useState([]);
  // State manager
  const { loading, registers, allfilters, total, deleteRoles } = useSelector(
    (state) => state.roleRedux
  );
  const {
    loadingCurrentSubUser,
    currentSubUser
  } = useSelector(
      (state) => state.subUserRedux
  );
  const exportResult = useSelector(state => state.commomRedux.exportResult);
  const exportName = useSelector(state => state.commomRedux.exportName);
  const { user } = useSelector((state) => state.securityRedux);

  // Permissions
  const editablePermissions = HasPermission(user, "", [333]);

  const pagination = usePagination(tableRef, searchRoles, allfilters);

  const handleClickMenu = (type, role) => {
    if (type === messages['edit']) {
      setRoleSelected(role);
      setShowModalCrud(true);
    }
    else if (type === messages['delete']) {
      setRolesToRemove([role]);
      setShowModalDelete(true);
    }
  };

  const exportPermissions = (modules, category) => {
    let result = '';
    let modulesFilter = modules.filter((item) => item.category === category)
    modulesFilter.forEach((module, index) => {
      result = result + (index ? `, ${module.name}` : `${module.name}`)
    })
    return result
  }

  useEffect(() => {
    if(user?.isSubUser && user?.subUserId) dispatch(getCurrentSubUser({id: user?.subUserId}))
  }, [])

  useEffect(() => {
    if (total) {
      pagination.setTotal(total);
    }
  }, [total]);

  useEffect(() => {
    pagination.setColumns([
      {
        key: 'name',
        show: true,
        title: messages['role_role'],
        export: true,
        width: 350,
        render: (role) => {
          return (
            <Grid
              container
              alignItems="center"
              justifyContent="flex-start"
              className="context-menu-container"
              wrap="nowrap"
            >
              <Grid item zeroMinWidth>
                {role?.name}
              </Grid>
              {editablePermissions &&
                <Grid item>
                  <MenuComponent
                    options={[messages['edit'], messages['delete']]}
                    icon={'more_vert'}
                    className="context-menu"
                    onClick={handleClickMenu}
                    value={role}
                  />
                </Grid>
              }
            </Grid>
          );
        },
        functionString: 'return row?.name || ""'
      },
      {
        key: 'permission',
        show: true,
        title: messages['role_permissions'],
        exportValue: (role) => exportPermissions(role.modules ? role.modules : [], 'Permission'),
        export: true,
        render: (role) => {
          let permissions = role?.modules ? [
            ...role?.modules?.filter((item) => item.category === 'Permission'),
            ...role?.modules?.filter((item) => item.category === 'Report')
          ] : []
          return (
            <div id="modules">
              {
                permissions?.map((item, index) =>
                  <Pill key={`pill-${index}`} style={{ margin: '2px 0px' }} item={{ name: item.name, color: styles.blue }} />)
              }
              {
                useSupervisorPermission(user, currentSubUser) && role?.isSupervisor && <Pill key='pill-supervisor' style={{ margin: '2px 0px' }} item={{ name: messages['role_supervisor'], color: styles.blue }} />
              }

            </div>
          );
        },
        functionString: useSupervisorPermission(user, currentSubUser) ?
        `return row?.isSupervisor ? row?.modules?.map(item => item?.name).join(" / ") + " / ${messages['role_supervisor']} " : row?.modules?.map(item => item?.name).join(" / ")` :
        `return row?.modules?.map(item => item?.name).join(" / ")`
      },
    ]);
    pagination.onChangePage();
  }, [locale, currentSubUser]);

  useEffect(() => {
    if (exportResult && exportName === "roles") {
      NotificationManager.info(
        messages['exportInProcessBody'],
        messages['exportInProcessTitle'],
        4000,
        null,
        null,
        'info',
        null
      );
      dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
    }
  }, [exportResult, exportName]);

  useEffect(() => {
    if (deleteRoles) {
      NotificationManager.success(
        messages[`role_deleteMessage`],
        messages['success'],
        3000,
        null,
        null,
        'Success',
      );
      pagination?.setSearchText("")
    }
  }, [deleteRoles]);

  return (
    <Container maxWidth={false}>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Grid item xs={12} sm={4} style={{ display: "flex" }}>
          <Toolbar>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
            >
              {editablePermissions &&
                <Grid item>
                  <Button
                    type="icon"
                    icon="add_box"
                    placement="bottom"
                    tooltip={messages['add']}
                    onClick={() => setShowModalCrud(true)}
                  />
                </Grid>
              }
              <Grid item>
                <TableButtons
                  tableRef={tableRef}
                  messages={messages}
                  buttons={['export']}
                  onExport={(type) => {
                    if (pagination?.selectedItems?.length) {
                      if (type === "Csv") {
                        tableRef.current.exportCSV();
                      } else if (type === "Pdf") {
                        tableRef.current.exportPDF();
                      }
                    } else {
                      const columns = tableRef.current?.columns
                      const headers = columns.filter(x => x.show && x.export).map(x => { return { header: x.title, column: x.key, functionString: x.functionString } }) || [];

                      dispatch(sendGenericExport(
                        "roles",
                        type,
                        `
                        query searchRoles ($openSearch: String, $limit: Int, $offset: Int) {
                          searchRoles (openSearch: $openSearch, limit: $limit, offset: $offset) {
                              items {
                                  id
                                  name
                                  description
                                  isSupervisor
                                  modules {
                                      id
                                      code
                                      userCode
                                      name
                                      groupName
                                      parentModuleId
                                      category
                                      subModules {
                                          id
                                          code
                                          userCode
                                          name
                                          groupName
                                          parentModuleId
                                          category
                                          subModules {
                                              id
                                              code
                                              userCode
                                              name
                                              groupName
                                              parentModuleId
                                              category
                                          }
                                      }
                                  }
                              }
                              total
                          }
                        }
                        `,
                        { openSearch: pagination?.searchText },
                        headers,
                        { unit: 'pt', size: 'A4', orientation: 'landscape', fontSize: 10 }
                      ));
                    }
                  }}
                />
              </Grid>
              {
                editablePermissions && pagination?.selectedItems?.length > 0 &&
                <Grid item>
                  <Button
                    type="icon"
                    icon="delete"
                    tooltip={messages['delete']}
                    disabled={!pagination?.selectedItems?.length}
                    onClick={() => {
                      setRolesToRemove(pagination?.selectedItems);
                      setShowModalDelete(true);
                    }}
                  />
                </Grid>
              }
            </Grid>
          </Toolbar>
        </Grid>
        <Grid item xs={12} sm={4} className="mb-10 text-right">
          <SearchInput
            placeholder={messages['search']}
            {...pagination}
          />
        </Grid>
      </Grid>
      <Table
        ref={tableRef}
        keyColumn="id"
        loading={loading || loadingCurrentSubUser}
        topheight={235}
        data={registers}
        exportOrientation='landscape'
        exportName='account-roles'
        {...pagination}
        hideDragable
      />
      {
        showModalCrud &&
        <CreditEditRol
          onShow={showModalCrud}
          onClose={(saved) => {
            setShowModalCrud(false);
            setRoleSelected();
            if (saved) {
              dispatch(searchRoles({
                ...allfilters,
                openSearch:pagination?.searchText
              }));
            }
          }}
          role={roleSelected}
        />
      }
      {
        showModalDelete &&
        <DeleteRole
          onShow={showModalDelete}
          roles={rolesToRemove}
          onClose={(saved) => {
            setShowModalDelete(false);
            if (saved) {
              setRolesToRemove([]);
              dispatch(searchRoles({
                ...allfilters,
                openSearch: ''
              }));
            }
          }}
        />
      }
    </Container>
  );
}

export default Role;